import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistoryCustomBack } from 'weblite-router'
import * as toast from '@weblite/react-toastify'
import { LIMITS } from '@weblite/permission'
// Material
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { WEastIcon } from '@weblite/icons'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
// Common
import { Label } from '$common/label'
import { TextInput } from '$common/input'
import { PrimaryButton } from '$common/buttons'
import {
  showInvalidLinkSnackbar,
  showLoadingWithTextSnackbar,
} from '$comp/snackbars'
import { showInvalidLinkDialog } from '$comp/modals/invalid-link'
import { canJoinHallUsingLinkOrIdentifier, JoinFnType } from './helper'
import { SelectRole } from '$comp/_app/components/leftPanel/components/select-role'
import { signUpUser } from '$logic/auth'
import {
  closeAuthSignup,
  goToAuthPrivacyPolicy,
  goToAuthSignup,
} from '$logic/router'
import {
  ACCEPTABLE_STUDENT_VERSION,
  ACCEPTABLE_TEACHER_VERSION,
  createProfile,
  editProfile,
  OrganizerType,
  UserRole,
} from '$logic/profile'
import { clientUserStorage } from '$logic/client-user/storage'
import { addHallDependencies } from '$logic/subscription'
import { updateClientUser } from '$logic/client-user'
import hostConf from '$config/frontend'
import { t } from '$i18n'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { SelectOrganizerType } from './select-organizer-type'

const { userConstraints } = LIMITS

const StyledStack = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.white,
  height: '100%',
  padding: '8px 24px 24px ',
  zIndex: 2,
  borderRadius: '25px 25px 0 0',
  width: '100%',
  overflowY: 'auto',
}))

interface Props {
  name?: string
  lastName?: string
  invited: boolean
  role?: UserRole | null
  organizerType?: OrganizerType
}

const VITE_SIGNUP_MODE = hostConf.VITE_SIGNUP_MODE || ''
const SHOW_REF_CODE_BOX = VITE_SIGNUP_MODE.toUpperCase().includes('INVITE')

const LINK_BOX_EMPTY_SPACE =
  20 + // link title height
  8 + // link title margin bottom
  48 + // link input height
  16 // link input margin bottom

export function SignUp({
  name: defaultName,
  lastName: defaultLastname,
  invited,
  role: defaultRole,
  organizerType: defaultOrganizerType,
}: Props) {
  const [name, setName] = useState(defaultName || '')
  const [lastname, setLastname] = useState(defaultLastname || '')
  const [nameIsEmpty, setNameIsEmpty] = useState<boolean>(false)

  const [referrerCode, setReferrerCode] = useState('')
  const [refCodeError, setRefCodeError] = useState('')
  const [organizerType, setOrganizerType] = useState<OrganizerType | undefined>(
    defaultOrganizerType,
  )

  const [role, setRole] = useState<UserRole | null>(defaultRole || null)
  const [linkOrIdentifier, setLinkOrIdentifier] = useState('')

  const [loading, setLoading] = useState(false)

  const alreadySignedUpRef = useRef(false)
  const joinFnRef = useRef<JoinFnType>()

  const isDesktop = useIsDesktopSize()

  const refCodeIsRequired =
    VITE_SIGNUP_MODE === 'Invite&RefCode' ||
    (VITE_SIGNUP_MODE === 'Invite|RefCode' && !invited) ||
    VITE_SIGNUP_MODE === 'InviteOnly'

  const onSignUpClick = useCallback(
    async (shouldValidateLinkArg = true) => {
      const shouldValidateLink = shouldValidateLinkArg && role === 'student'
      joinFnRef.current = undefined
      const showInvalidLinkDialogFn = () =>
        void showInvalidLinkDialog({
          onConfirm: closeCurrentModal,
          onCancel: () => {
            closeCurrentModal()
            return onSignUpClick(false)
          },
        })

      if (shouldValidateLink && linkOrIdentifier) {
        const { joinFn, canJoin } =
          await canJoinHallUsingLinkOrIdentifier(linkOrIdentifier)

        if (!canJoin) {
          showInvalidLinkDialogFn()
          return
        }

        joinFnRef.current = joinFn
      }

      setNameIsEmpty(!name)
      if (!name) return
      setLoading(true)

      try {
        if (!alreadySignedUpRef.current) {
          const errorCode = await signUpUser(
            name,
            lastname,
            {},
            referrerCode.trim(),
          )

          if (errorCode) {
            setRefCodeError(t(`signup.errorCode.${errorCode}`))
            return
          }
        } else await updateClientUser({ firstname: name, lastname })

        const clientUser = clientUserStorage.get()
        const profileToSubmit = {
          school:
            role === UserRole.TEACHER
              ? {
                  role: organizerType!,
                  v: ACCEPTABLE_TEACHER_VERSION,
                }
              : {
                  role: role!,
                  v: ACCEPTABLE_STUDENT_VERSION,
                },
        }

        if (alreadySignedUpRef.current) await editProfile(profileToSubmit)
        else await createProfile(profileToSubmit, clientUser)

        alreadySignedUpRef.current = true

        if (shouldValidateLink && linkOrIdentifier) {
          const joiningHallToastId = showLoadingWithTextSnackbar(
            t('signup.joiningInHall'),
          )

          const res = await joinFnRef.current?.(linkOrIdentifier)
          if (!res || res.error) {
            if (joiningHallToastId) toast.dismiss(joiningHallToastId)
            showInvalidLinkSnackbar()
          } else await addHallDependencies(res.payload)
        }
      } finally {
        setLoading(false)
      }
    },
    [name, role, lastname, referrerCode, linkOrIdentifier, organizerType],
  )

  useEffect(() => {
    setRefCodeError('')
  }, [name, lastname, referrerCode])

  const handleGoToPrivacyPolicy = useCallback(() => {
    if (loading) return
    goToAuthPrivacyPolicy(() => {
      goToAuthSignup({ name, lastName: lastname, invited, role, organizerType })
    })
  }, [name, lastname, loading, role, invited, organizerType])

  useHistoryCustomBack(() => void closeAuthSignup(), [])

  return (
    <StyledStack>
      <Box
        alignItems="center"
        sx={{ display: 'flex', marginBottom: 3, marginRight: '-16px' }}
      >
        <IconButton size="large" onClick={closeAuthSignup}>
          <WEastIcon sx={{ color: 'neutral8000' }} />
        </IconButton>
        <Typography color="secondary700" variant="h3">
          {t('signup.title')}
        </Typography>
      </Box>

      <Box sx={{ marginBottom: 'auto' }}>
        <Label title={t('user.name')} required />
        <TextInput
          placeholder={t('user.enterName')}
          value={name}
          onChange={newName => setName(newName?.trimStart() || '')}
          hasError={nameIsEmpty}
          errorMessage={t('signup.emptyName')}
          maxLength={userConstraints.firstname.maxLength}
        />

        <Label title={t('user.lastName')} />
        <TextInput
          placeholder={t('user.enterLastName')}
          value={lastname}
          onChange={setLastname}
          maxLength={userConstraints.lastname.maxLength}
        />

        {SHOW_REF_CODE_BOX && (
          <>
            <Label title={t('signup.referrerCode')(!refCodeIsRequired)} />
            <TextInput
              placeholder={t('signup.insertReferrerCode')}
              value={referrerCode}
              onChange={setReferrerCode}
              errorMessage={refCodeError}
              hasError={!!refCodeError}
            />
          </>
        )}

        <SelectRole value={role} onChange={setRole} />

        {role === UserRole.TEACHER && (
          <SelectOrganizerType
            value={organizerType}
            onChange={val => setOrganizerType(val)}
          />
        )}

        {role === 'student' && (
          <>
            <Label title={t('signup.linkOrIdentifier')} />
            <TextInput
              placeholder={t('common.optional')}
              value={linkOrIdentifier}
              maxLength={userConstraints.lastname.maxLength}
              onChange={newIdentifier =>
                setLinkOrIdentifier(newIdentifier?.trim() || '')
              }
            />
          </>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: '2px',
          marginBottom: 2,
          marginTop:
            isDesktop && role !== 'student'
              ? `${LINK_BOX_EMPTY_SPACE}px`
              : 'auto',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
        width="100%"
      >
        <Typography variant="paragraph3">
          {t('signup.withSigningUp')}
        </Typography>
        <Typography
          variant="paragraph3"
          color="primary700"
          sx={{ textDecoration: 'underline', cursor: 'pointer' }}
          onClick={handleGoToPrivacyPolicy}
        >
          {t('common.termsAndConditions')}
        </Typography>
        <Typography variant="paragraph3">
          {t('signup.haveReadAndAccept')}
        </Typography>
      </Box>

      <PrimaryButton
        size="medium"
        variant="contained"
        sx={{ backgroundColor: 'primary500' }}
        onClick={onSignUpClick}
        text={
          loading ? (
            <CircularProgress size={20} color="primary" />
          ) : (
            t('common.resume')
          )
        }
        disabled={
          !name ||
          loading ||
          (refCodeIsRequired && !referrerCode.trim()) ||
          !role ||
          (role === UserRole.TEACHER && !organizerType)
        }
      />
    </StyledStack>
  )
}
